import { useState } from 'react';

export interface SurveillanceFiche {
  nom_prenoms: string;
  date_entree: string;
  date_sortie: string;
  sage_femme: string;
  date_naissance: string;
  heure_naissance: string;
  duree_sejour: string;
  immediate: Record<string, Record<string, string>>;
  jour1: Record<string, Record<string, string>>;
  jour2: Record<string, Record<string, string>>;
}

export const emptySurveillanceFiche: SurveillanceFiche = {
  nom_prenoms: '',
  date_entree: '',
  date_sortie: '',
  sage_femme: '',
  date_naissance: '',
  heure_naissance: '',
  duree_sejour: '',
  immediate: {},
  jour1: {},
  jour2: {},
};

const HEURES_IMMEDIATES = [
  '15mn', '30mn', '45mn', '60mn',
  '1h00', '1h15', '1h30', '1h45',
  '2h00', '2h15', '2h30', '2h45',
  '3h00', '4h00', '5h00', '6h00',
];

const HEURES_JOUR = ['H4', 'H8', 'H12', 'H16', 'H20', 'H24'];

const PARAMETRES_MERE = [
  { key: 'mere_pouls', label: 'Pouls' },
  { key: 'mere_ta', label: 'TA' },
  { key: 'mere_temperature', label: 'Température' },
  { key: 'mere_respiration', label: 'Respiration' },
  { key: 'mere_evacuation_temp', label: 'Evacuer la temp. (normale, chaud, froid)' },
  { key: 'mere_coloration', label: 'Coloration (anormale, Rose)' },
  { key: 'mere_frequence', label: 'Frequence (anormale, difficile)' },
  { key: 'mere_etat_neuro', label: 'Etat neurologique (Eveillé, Tonus, Reflexes)' },
  { key: 'mere_ictère', label: 'Ictère' },
];

const PARAMETRES_BEBE = [
  { key: 'bebe_poids', label: 'Poids' },
  { key: 'bebe_ta', label: 'TA' },
  { key: 'bebe_temperature', label: 'Température' },
  { key: 'bebe_respiration', label: 'Respiration' },
  { key: 'bebe_evacuation_temp', label: 'Evacuer la temp. (normale, chaud, froid)' },
  { key: 'bebe_coloration', label: 'Coloration (anormale, Rose)' },
  { key: 'bebe_frequence', label: 'Frequence (anormale, difficile)' },
  { key: 'bebe_etat_neuro', label: 'Etat neurologique (Eveillé, Tonus, Reflexes)' },
  { key: 'bebe_ictère', label: 'Ictère' },
  { key: 'bebe_comportement_sein', label: 'Comportement au sein' },
  { key: 'bebe_saignement_cordon', label: 'Saignement cordon ombilical (Oui; Non)' },
  { key: 'bebe_emission_urines', label: 'Emission d\'urines' },
  { key: 'bebe_emission_meconium', label: 'Emission meconium' },
  { key: 'bebe_autres_soins', label: 'Autres soins' },
  { key: 'bebe_actions', label: 'Actions (Inscrire x dans la colonne)' },
];

interface Props {
  data: SurveillanceFiche;
  onChange: (data: SurveillanceFiche) => void;
}

function getCell(
  data: SurveillanceFiche,
  periode: 'immediate' | 'jour1' | 'jour2',
  heure: string,
  key: string,
): string {
  return data[periode]?.[heure]?.[key] ?? '';
}

function setCell(
  prev: SurveillanceFiche,
  periode: 'immediate' | 'jour1' | 'jour2',
  heure: string,
  key: string,
  value: string,
): SurveillanceFiche {
  return {
    ...prev,
    [periode]: {
      ...prev[periode],
      [heure]: {
        ...prev[periode][heure],
        [key]: value,
      },
    },
  };
}

function SurveillanceTable({
  title,
  data,
  periode,
  heures,
  parametres,
  onCellChange,
  note,
}: {
  title: string;
  data: SurveillanceFiche;
  periode: 'immediate' | 'jour1' | 'jour2';
  heures: string[];
  parametres: { key: string; label: string }[];
  onCellChange: (periode: 'immediate' | 'jour1' | 'jour2', heure: string, key: string, value: string) => void;
  note?: string;
}) {
  const inputCls = 'w-full text-center text-xs py-1 px-0.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-rose-300 bg-white';
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-bold text-gray-700">{title}</h4>
        {note && <span className="text-xs text-gray-400 italic">{note}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-gray-200 rounded-lg">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-2 py-2 text-left font-semibold text-gray-600 border-r border-gray-200 min-w-[200px] sticky left-0 bg-gray-50 z-10">
                Paramètres
              </th>
              {heures.map(h => (
                <th key={h} className="px-1 py-2 text-center font-semibold text-gray-600 border-r border-gray-200 min-w-[48px]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parametres.map((p, idx) => (
              <tr key={p.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                <td className="px-2 py-1.5 text-gray-700 border-r border-gray-200 border-t border-gray-100 sticky left-0 bg-inherit z-10">
                  {p.label}
                </td>
                {heures.map(h => (
                  <td key={h} className="px-0.5 py-1 border-r border-gray-100 border-t border-gray-100">
                    <input
                      type="text"
                      className={inputCls}
                      value={getCell(data, periode, h, p.key)}
                      onChange={e => onCellChange(periode, h, p.key, e.target.value)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function FicheSurveillanceSection({ data, onChange }: Props) {
  const [local, setLocal] = useState<SurveillanceFiche>(data);

  const handleFieldChange = (field: keyof SurveillanceFiche, value: string) => {
    const next = { ...local, [field]: value };
    setLocal(next);
    onChange(next);
  };

  const handleCellChange = (
    periode: 'immediate' | 'jour1' | 'jour2',
    heure: string,
    key: string,
    value: string,
  ) => {
    const next = setCell(local, periode, heure, key, value);
    setLocal(next);
    onChange(next);
  };

  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 bg-white text-gray-800 placeholder-gray-400';
  const labelClass = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="space-y-6">
      {/* Infos générales */}
      <div className="bg-rose-50/50 rounded-xl border border-rose-100 p-4">
        <h3 className="text-sm font-bold text-rose-700 mb-3 flex items-center gap-2">
          <i className="ri-file-list-line"></i>
          Informations de la fiche
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className={labelClass}>Nom et prénoms de l&apos;accouchée</label>
            <input
              type="text"
              className={inputClass}
              value={local.nom_prenoms}
              onChange={e => handleFieldChange('nom_prenoms', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Date d&apos;entrée</label>
            <input
              type="date"
              className={inputClass}
              value={local.date_entree}
              onChange={e => handleFieldChange('date_entree', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Date de sortie</label>
            <input
              type="date"
              className={inputClass}
              value={local.date_sortie}
              onChange={e => handleFieldChange('date_sortie', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Sage-Femme / Infirmière responsable</label>
            <input
              type="text"
              className={inputClass}
              value={local.sage_femme}
              onChange={e => handleFieldChange('sage_femme', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Date de naissance</label>
            <input
              type="date"
              className={inputClass}
              value={local.date_naissance}
              onChange={e => handleFieldChange('date_naissance', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Heure de naissance</label>
            <input
              type="time"
              className={inputClass}
              value={local.heure_naissance}
              onChange={e => handleFieldChange('heure_naissance', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Durée du séjour</label>
            <input
              type="text"
              className={inputClass}
              value={local.duree_sejour}
              onChange={e => handleFieldChange('duree_sejour', e.target.value)}
              placeholder="ex: 48 heures"
            />
          </div>
        </div>
      </div>

      {/* Note astérisque */}
      <div className="bg-amber-50/50 rounded-lg border border-amber-100 p-3">
        <p className="text-xs text-amber-700">
          <i className="ri-information-line mr-1"></i>
          La prise des constantes n&apos;est obligatoire que dans les colonnes contenant une astérisque (*).
          Saisissez les valeurs relevées pour chaque paramètre et chaque horaire.
        </p>
      </div>

      {/* Surveillance immédiate */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-time-line text-rose-500"></i>
          Surveillance Immédiate (Post-partum)
        </h3>
        <SurveillanceTable
          title="Mère"
          data={local}
          periode="immediate"
          heures={HEURES_IMMEDIATES}
          parametres={PARAMETRES_MERE}
          onCellChange={handleCellChange}
        />
        <SurveillanceTable
          title="Nouveau-né"
          data={local}
          periode="immediate"
          heures={HEURES_IMMEDIATES}
          parametres={PARAMETRES_BEBE}
          onCellChange={handleCellChange}
        />
      </div>

      {/* Surveillance Jour 1 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-calendar-line text-rose-500"></i>
          Surveillance Jour 1
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className={labelClass}>Date Jour 1</label>
            <input
              type="date"
              className={inputClass}
              value={local.jour1?._date ?? ''}
              onChange={e => {
                const next = {
                  ...local,
                  jour1: { ...local.jour1, _date: e.target.value },
                };
                setLocal(next);
                onChange(next);
              }}
            />
          </div>
        </div>
        <SurveillanceTable
          title="Mère"
          data={local}
          periode="jour1"
          heures={HEURES_JOUR}
          parametres={PARAMETRES_MERE}
          onCellChange={handleCellChange}
        />
        <SurveillanceTable
          title="Nouveau-né"
          data={local}
          periode="jour1"
          heures={HEURES_JOUR}
          parametres={PARAMETRES_BEBE}
          onCellChange={handleCellChange}
        />
      </div>

      {/* Surveillance Jour 2 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-calendar-line text-rose-500"></i>
          Surveillance Jour 2
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className={labelClass}>Date Jour 2</label>
            <input
              type="date"
              className={inputClass}
              value={local.jour2?._date ?? ''}
              onChange={e => {
                const next = {
                  ...local,
                  jour2: { ...local.jour2, _date: e.target.value },
                };
                setLocal(next);
                onChange(next);
              }}
            />
          </div>
        </div>
        <SurveillanceTable
          title="Mère"
          data={local}
          periode="jour2"
          heures={HEURES_JOUR}
          parametres={PARAMETRES_MERE}
          onCellChange={handleCellChange}
        />
        <SurveillanceTable
          title="Nouveau-né"
          data={local}
          periode="jour2"
          heures={HEURES_JOUR}
          parametres={PARAMETRES_BEBE}
          onCellChange={handleCellChange}
        />
      </div>
    </div>
  );
}